import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { differenceInDays } from 'date-fns'
import { ArrowLeft, Edit } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MaintenanceFeesList } from '../../components/features/maintenance'
import PropertyStats from '../../components/features/properties/PropertyStats'
import ReservationList, {
  type ReservationWithClient,
} from '../../components/features/properties/ReservationList'
import { supabase } from '../../lib/supabase'
import type {
  Address,
  ImageJson,
  Property,
  StProperty,
} from '../../types/index'

export interface PropertyStats {
  totalReservations: number
  totalRevenue: number
  occupancyRate: number
  averageStayDuration: number
  nextCheckIn: ReservationWithClient | null
  recentReservations: ReservationWithClient[]
  monthlyRevenue: number
  lastMonthRevenue: number
}

export function PropertyDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [property, setProperty] = useState<Property | null>(null)
  const [stats, setStats] = useState<PropertyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editedProperty, setEditedProperty] = useState<Property | null>(null)

  useEffect(() => {
    if (id) {
      loadPropertyDetails(id)
    }
  }, [id])

  useEffect(() => {
    if (property) {
      setEditedProperty({ ...property })
    }
  }, [property])

  const loadPropertyDetails = async (propertyId: string) => {
    setLoading(true)
    setError(null)

    try {
      // Carregar dados da propriedade
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .select(
          '*, images: property_images(*), amenities: property_amenities(*, amenity: st_listing_amenities(*)), stays_property: st_properties(*, images: st_properties_images(*))'
        )
        .eq('id', propertyId)
        .single()

      if (propertyError) throw propertyError

      const processedProperty = {
        ...propertyData,
        address: propertyData.address as Address,
        amenities:
          propertyData.amenities?.map(
            (a: any) =>
              a.amenity?._mstitle?.['pt-BR'] || 'Amenity name not found'
          ) || [],
        stays_property: propertyData.stays_property
          ? ({
              ...propertyData.stays_property,
              images: (propertyData.stays_property.images || []) as ImageJson[],
            } as StProperty)
          : null,
      }

      // Carregar reservas da propriedade com informações do cliente
      const { data: reservationsData } = await supabase
        .from('reservations')
        .select(
          `
          *,
          client: clients(name, email, phone)
        `
        )
        .eq('property_id', propertyId)
        .order('check_in_date', { ascending: false }) // Ordenar por data de check-in (mais recente primeiro)

      // Calcular estatísticas
      const currentDate = new Date()
      const thirtyDaysAgo = new Date(currentDate)
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const sixtyDaysAgo = new Date(currentDate)
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)

      const allReservations = reservationsData || []
      const activeReservations = allReservations.filter(
        (r) => r.type !== 'cancelled'
      )

      // Reservas dos últimos 30 dias
      const recentReservations = activeReservations.filter(
        (r) => r.created_at && new Date(r.created_at) >= thirtyDaysAgo
      )

      // Reservas do mês anterior (30-60 dias atrás)
      const lastMonthReservations = activeReservations.filter((r) => {
        if (!r.created_at) return false
        const createdAt = new Date(r.created_at)
        return createdAt >= sixtyDaysAgo && createdAt < thirtyDaysAgo
      })

      // Calcular receita
      const monthlyRevenue = recentReservations.reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )
      const lastMonthRevenue = lastMonthReservations.reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )
      const totalRevenue = activeReservations.reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )

      // Calcular duração média das estadias
      const averageStayDuration =
        activeReservations.length > 0
          ? activeReservations.reduce((sum, r) => {
              const checkIn = new Date(r.check_in_date)
              const checkOut = new Date(r.check_out_date)
              return sum + differenceInDays(checkOut, checkIn)
            }, 0) / activeReservations.length
          : 0

      // Calcular taxa de ocupação (últimos 30 dias)
      const totalDays = 30
      const occupiedDays = recentReservations.reduce((sum, r) => {
        const checkIn = new Date(r.check_in_date)
        const checkOut = new Date(r.check_out_date)
        const start = checkIn > thirtyDaysAgo ? checkIn : thirtyDaysAgo
        const end = checkOut < currentDate ? checkOut : currentDate
        return sum + Math.max(0, differenceInDays(end, start))
      }, 0)
      const occupancyRate = (occupiedDays / totalDays) * 100

      // Próximo check-in - buscar a próxima reserva com status waiting_checkin
      const upcomingReservations = activeReservations
        .filter((r) => r.status === 'waiting_checkin')
        .sort(
          (a, b) =>
            new Date(a.check_in_date).getTime() -
            new Date(b.check_in_date).getTime()
        )

      const propertyStats: PropertyStats = {
        totalReservations: activeReservations.length,
        totalRevenue,
        occupancyRate,
        averageStayDuration,
        nextCheckIn:
          upcomingReservations.length > 0
            ? (upcomingReservations[0] as unknown as ReservationWithClient)
            : null,
        recentReservations: allReservations.slice(
          0,
          5
        ) as unknown as ReservationWithClient[], // Já ordenadas por check_in_date desc
        monthlyRevenue,
        lastMonthRevenue,
      }

      setProperty(processedProperty as Property)
      setStats(propertyStats)
    } catch (err: unknown) {
      console.error('Error loading property details:', err)
      setError(
        err instanceof Error
          ? err.message
          : 'Erro ao carregar detalhes da acomodação.'
      )
    } finally {
      setLoading(false)
    }
  }

  const mainImageSrc = useMemo(() => {
    if (!property) return undefined
    if (property.images && property.images.length > 0) {
      return (
        property.images.find((img) => img.is_primary)?.url ||
        property.images[0].url
      )
    }
    return property.stays_main_image || undefined
  }, [property])

  const handleInputChange = (field: string, value: unknown) => {
    if (!editedProperty) return
    setEditedProperty((prev) => (prev ? { ...prev, [field]: value } : null))
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green'
      case 'inactive':
        return 'red'
      case 'draft':
        return 'yellow'
      case 'hidden':
        return 'gray'
      default:
        return 'gray'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo'
      case 'inactive':
        return 'Inativo'
      case 'draft':
        return 'Rascunho'
      case 'hidden':
        return 'Oculto'
      default:
        return status
    }
  }

  // Componente para renderizar campos de visualização
  const ViewField = ({
    label,
    value,
    type = 'text',
  }: {
    label: string
    value: unknown
    type?: string
  }) => {
    if (type === 'currency') {
      return (
        <Box mb={4}>
          <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
            {label}
          </Text>
          <Text fontSize="md" color="gray.900">
            {value ? formatCurrency(Number(value)) : '-'}
          </Text>
        </Box>
      )
    }

    if (type === 'percentage') {
      return (
        <Box mb={4}>
          <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
            {label}
          </Text>
          <Text fontSize="md" color="gray.900">
            {value ? `${value}%` : '-'}
          </Text>
        </Box>
      )
    }

    return (
      <Box mb={4}>
        <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
          {label}
        </Text>
        <Text fontSize="md" color="gray.900">
          {value ? String(value) : '-'}
        </Text>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box w="100%" minH="100vh" py={8} px={6}>
        <VStack spacing={8}>
          <HStack>
            <Skeleton height="40px" width="40px" borderRadius="md" />
            <Skeleton height="32px" width="200px" />
          </HStack>
          <Skeleton height="300px" borderRadius="lg" mt={8} />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mt={8}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="120px" borderRadius="lg" />
            ))}
          </SimpleGrid>
        </VStack>
      </Box>
    )
  }

  if (error || !property || !editedProperty) {
    return (
      <Box w="100%" minH="100vh" py={8} px={6}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Erro ao carregar acomodação</AlertTitle>
            <AlertDescription>
              {error || 'Acomodação não encontrada'}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    )
  }

  return (
    <Box w="100%" minH="100vh">
      {/* Header */}
      <Box
        py={6}
        px={6}
        bg="white"
        borderBottomWidth="1px"
        borderColor="gray.200"
      >
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft />}
              onClick={() => navigate('/properties')}
              variant="ghost"
              size="lg"
            />
            <Box>
              <Heading size="lg" mb={1}>
                {property.title}
              </Heading>
              <HStack spacing={2}>
                <Badge
                  colorScheme={getStatusColor(property.status)}
                  px={3}
                  py={1}
                  borderRadius="lg"
                  textTransform="none"
                >
                  {getStatusLabel(property.status)}
                </Badge>
                <Text color="gray.600" fontSize="sm">
                  Código: {property.code}
                </Text>
              </HStack>
            </Box>
          </HStack>
        </HStack>
      </Box>

      {/* Imagem Principal - 300px de altura */}
      <Box w="100%" h="300px" overflow="hidden">
        <Image
          src={mainImageSrc}
          alt={property.title}
          objectFit="cover"
          w="100%"
          h="100%"
          fallbackSrc="https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=1200"
        />
      </Box>

      {/* Conteúdo Principal */}
      <Box px={6} py={8}>
        {/* Estatísticas */}
        <PropertyStats stats={stats as PropertyStats} loading={loading} />

        {/* Alert de Erro */}

        {/* Layout 70/30 */}
        <Grid templateColumns="70fr 30fr" gap={8} h="auto">
          {/* Informações da Acomodação - 70% */}
          <GridItem>
            <Card h="fit-content">
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md">Informações da Acomodação</Heading>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="solid"
                      onClick={() =>
                        navigate('/properties/edit/' + property.id)
                      }
                      leftIcon={<Edit size={16} />}
                    >
                      Editar Informações
                    </Button>
                  </HStack>
                </Flex>

                <Tabs>
                  <TabList>
                    <Tab>Informações Básicas</Tab>
                    <Tab>Detalhes</Tab>
                    <Tab>Acesso</Tab>
                    <Tab>Custos</Tab>
                    <Tab>Manutenção</Tab>
                    <Tab>Instruções</Tab>
                  </TabList>

                  <TabPanels>
                    {/* Tab 1: Informações Básicas */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        {/* Status */}
                        <FormControl display="flex" alignItems="center">
                          <FormLabel htmlFor="status" mb="0">
                            Acomodação ativa
                          </FormLabel>
                          <Switch
                            id="status"
                            isChecked={editedProperty.status === 'active'}
                            onChange={(e) =>
                              handleInputChange(
                                'status',
                                e.target.checked ? 'active' : 'inactive'
                              )
                            }
                            colorScheme="green"
                            isDisabled={true} // Desabilitado para visualização
                          />
                        </FormControl>

                        {/* Código */}

                        <ViewField
                          label="Código da Acomodação"
                          value={property.code}
                        />

                        {/* Título */}
                        <ViewField label="Título" value={property.title} />

                        {/* Descrição */}
                        <ViewField
                          label="Descrição"
                          value={property.description}
                        />

                        {/* Endereço */}
                        <ViewField
                          label="Endereço"
                          value={`${property.address.street}, ${property.address.streetNumber}, ${property.address.region}`}
                        />

                        {/* Cidade, Estado, CEP */}
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                          <ViewField
                            label="Cidade"
                            value={property.address.city}
                          />
                          <ViewField
                            label="Estado"
                            value={property.address.state}
                          />
                          <ViewField label="CEP" value={property.address.zip} />
                        </SimpleGrid>

                        {/* Imagem Principal */}
                        <ViewField
                          label="URL da Imagem Principal"
                          value={property.stays_main_image}
                        />

                        {/* ID do Stays */}
                        <ViewField
                          label="ID do Stays"
                          value={property.stays_id}
                        />
                      </VStack>
                    </TabPanel>

                    {/* Tab 2: Detalhes */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        {/* Quartos, Banheiros, Hóspedes, Camas */}
                        <SimpleGrid
                          columns={{ base: 1, md: 2, lg: 4 }}
                          spacing={4}
                        >
                          <ViewField
                            label="Quartos"
                            value={property.bedrooms}
                          />
                          <ViewField
                            label="Banheiros"
                            value={property.bathrooms}
                          />
                          <ViewField
                            label="Máx. Hóspedes"
                            value={property.max_guests}
                          />
                          <ViewField label="Camas" value={property.beds} />
                        </SimpleGrid>

                        {/* Horários */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <ViewField
                            label="Horário de Check-in"
                            value={property.checkin_time}
                          />
                          <ViewField
                            label="Horário de Check-out"
                            value={property.checkout_time}
                          />
                        </SimpleGrid>

                        {/* Amenities */}
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                            mb={2}
                          >
                            Comodidades
                          </Text>
                          <SimpleGrid
                            columns={{ base: 2, md: 3, lg: 4 }}
                            spacing={4}
                          >
                            {property.amenities.map((amenity) => (
                              <Text
                                key={amenity}
                                fontSize="md"
                                color="gray.900"
                              >
                                {amenity}
                              </Text>
                            ))}
                          </SimpleGrid>
                        </Box>
                      </VStack>
                    </TabPanel>

                    {/* Tab 3: Acesso */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        {/* Wi-Fi */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <ViewField
                            label="Nome do Wi-Fi"
                            value={property.wifi_name}
                          />
                          <ViewField
                            label="Senha do Wi-Fi"
                            value={property.wifi_password ? '••••••••' : ''}
                          />
                        </SimpleGrid>

                        {/* Código de Acesso */}
                        <ViewField
                          label="Código de Acesso"
                          value={property.access_code}
                        />
                      </VStack>
                    </TabPanel>

                    {/* Tab 4: Custos */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color="gray.700"
                        >
                          Configurações de Custos e Gestão
                        </Text>

                        <VStack spacing={6} align="stretch">
                          <ViewField
                            label="Taxa de Limpeza"
                            value={property.cleaning_fee}
                            type="currency"
                          />
                          <ViewField
                            label="Percentual de Gestão"
                            value={property.management_cost_percentage}
                            type="percentage"
                          />
                        </VStack>
                      </VStack>
                    </TabPanel>

                    {/* Tab 5: Manutenção */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color="gray.700"
                        >
                          Histórico de Manutenção
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Registre e acompanhe todos os custos de manutenção e
                          reparos da acomodação.
                        </Text>

                        {/* Maintenance Fees List Component */}
                        <MaintenanceFeesList propertyId={property.id} />
                      </VStack>
                    </TabPanel>

                    {/* Tab 6: Instruções */}
                    <TabPanel>
                      <VStack spacing={6} align="stretch">
                        {/* Instruções de Check-in */}
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                            mb={2}
                          >
                            Instruções de Check-in
                          </Text>
                          <Text
                            fontSize="md"
                            color="gray.900"
                            whiteSpace="pre-wrap"
                          >
                            {property.checkin_instructions || '-'}
                          </Text>
                        </Box>

                        {/* Instruções de Check-out */}
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                            mb={2}
                          >
                            Instruções de Check-out
                          </Text>
                          <Text
                            fontSize="md"
                            color="gray.900"
                            whiteSpace="pre-wrap"
                          >
                            {property.checkout_instructions || '-'}
                          </Text>
                        </Box>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          </GridItem>

          {/* Sidebar - 30% */}
          <GridItem>
            <ReservationList
              reservations={stats?.recentReservations ?? []}
              nextCheckIn={stats?.nextCheckIn ?? null}
              loading={loading}
            />
          </GridItem>
        </Grid>
      </Box>
    </Box>
  )
}
